import React, { useState, useCallback } from 'react';
import { useReactFlow } from '@xyflow/react';
import Fuse from 'fuse.js';
import { useDiagramStore } from '../store/diagramStore';
import { NetworkZone, ConnectorType, ActorData } from '../types/diagram.types';
import { ZONE_COLORS, GROUP_COLORS } from '../constants/groupColors';
import { CONNECTOR_STYLES } from '../constants/connectorStyles';
import { ACTOR_NODES } from '../data/initialNodes';
import { PALETTE_GROUPS, SERVICE_PALETTE_MIME } from '../constants/palette';
import { hasAccess } from 'helpers/access';

const ZONE_LABELS: Record<NetworkZone, string> = {
  'zone-external': 'External',
  'zone-dmz': 'DMZ',
  'zone-kafka': 'Kafka (Z5)',
  'zone-internal': 'Internal',
  'zone-partners': 'Partners',
};

const CONNECTOR_TYPES: ConnectorType[] = [
  'REST', 'WebSocket', 'Kafka', 'PostgreSQL', 'Redis', 'TimescaleDB', 'S3', 'gRPC',
];
const ZONES: NetworkZone[] = [
  'zone-external', 'zone-dmz', 'zone-kafka', 'zone-internal', 'zone-partners',
];

const DB_SERVICES = [
  { id: 'svc-003', label: 'Users → PostgreSQL', dbName: 'users_db' },
  { id: 'svc-004', label: 'Accounts → PostgreSQL', dbName: 'accounts_db' },
  { id: 'svc-006', label: 'Orders → PostgreSQL', dbName: 'orders_db' },
  { id: 'svc-010', label: 'Quick Exchange → PostgreSQL', dbName: 'quick_exchange_db' },
  { id: 'svc-012', label: 'Courses → TimescaleDB', dbName: 'courses_db' },
  { id: 'svc-014', label: 'Acquiring GW → PostgreSQL', dbName: 'acquiring_db' },
  { id: 'svc-019', label: 'Crypto Acquiring → PostgreSQL', dbName: 'crypto_db' },
  { id: 'svc-020', label: 'Limiter → PostgreSQL', dbName: 'limiter_db' },
  { id: 'svc-021k', label: 'KYC → PostgreSQL', dbName: 'kyc_db' },
  { id: 'svc-023', label: 'Notifications → PostgreSQL', dbName: 'notifications_db' },
];

interface LeftPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  onAddActorClick: () => void;
}

const ToggleBtn: React.FC<{ isOpen: boolean; onClick: () => void }> = ({ isOpen, onClick }) => (
  <button
    onClick={onClick}
    title={isOpen ? 'Свернуть панель' : 'Развернуть панель'}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 24, height: 24, borderRadius: 4,
      border: '1px solid #E0E0E0', background: '#fff',
      cursor: 'pointer', color: '#757575', fontSize: 12, flexShrink: 0,
    }}
  >
    {isOpen ? '«' : '»'}
  </button>
);

const LeftPanel: React.FC<LeftPanelProps> = ({ isOpen, onToggle, onAddActorClick }) => {
  const {
    nodes, edges, flows, activeFlowId,
    zoneVisibility, connectorTypeVisibility, searchQuery,
    setActiveFlow, toggleZone, toggleConnectorType, setSearchQuery,
    dbLayerVisible, toggleDbLayer, setDbConnection,
    actorVisibility, customActors, toggleActorVisibility, removeCustomActor,
  } = useDiagramStore();

  // Flows affected by deprecated services
  const deprecatedIds = new Set(
    nodes.filter(n => n.type === 'service' && (n.data as any)?.status === 'deprecated').map(n => n.id)
  );
  const disabledFlowIds = new Set(
    flows
      .filter(f => f.edgeIds.some(eid => {
        const e = edges.find(ed => ed.id === eid);
        return e && (deprecatedIds.has(e.source) || deprecatedIds.has(e.target));
      }))
      .map(f => f.id)
  );

  const { fitView, setCenter } = useReactFlow();
  const [showPalette, setShowPalette] = useState(true);
  const canAddService = hasAccess('serviceConnectAdd');
  const [showFlows, setShowFlows] = useState(true);
  const [collapsedFlowGroups, setCollapsedFlowGroups] = useState<Set<string>>(new Set());
  const [showZones, setShowZones] = useState(true);
  const [showConnectors, setShowConnectors] = useState(true);
  const [showPartners, setShowPartners] = useState(true);
  const [showDbSection, setShowDbSection] = useState(false);
  const [showDbForm, setShowDbForm] = useState(false);
  const [dbFormServiceId, setDbFormServiceId] = useState('svc-003');
  const [dbHost, setDbHost] = useState('localhost');
  const [dbPort, setDbPort] = useState('5432');
  const [dbName, setDbName] = useState('');
  const [dbUser, setDbUser] = useState('postgres');
  const [dbPassword, setDbPassword] = useState('');
  const [dbConnecting, setDbConnecting] = useState(false);
  const [dbConnectStatus, setDbConnectStatus] = useState<string | null>(null);

  const serviceNodes = nodes.filter(n => n.type === 'service');
  const visibleCount = serviceNodes.filter(n => {
    const zone = (n.data as any)?.zone as NetworkZone;
    return zone ? zoneVisibility[zone] : true;
  }).length;

  const fuse = new Fuse(serviceNodes, {
    keys: ['data.name', 'data.group'], threshold: 0.35,
  });

  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q);
    if (!q.trim()) return;
    const results = fuse.search(q);
    if (results.length > 0) {
      const node = results[0].item;
      setCenter(node.position.x + 80, node.position.y + 40, { zoom: 1.4, duration: 600 });
    }
  }, [nodes, setSearchQuery, setCenter]); // eslint-disable-line

  const handleFlowClick = (id: string) => {
    setActiveFlow(activeFlowId === id ? null : id);
    if (activeFlowId !== id) {
      setTimeout(() => fitView({ duration: 500, padding: 0.1 }), 100);
    }
  };

  const handleDbConnect = async () => {
    setDbConnecting(true);
    setDbConnectStatus(null);
    try {
      const res = await fetch('/db-meta/schema', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ host: dbHost, port: parseInt(dbPort), dbname: dbName, user: dbUser, password: dbPassword, serviceId: dbFormServiceId }),
      });
      if (res.ok) {
        setDbConnection(dbFormServiceId, { host: dbHost, port: parseInt(dbPort), dbname: dbName, user: dbUser });
        setDbConnectStatus('connected');
      } else {
        setDbConnectStatus('static');
      }
    } catch {
      setDbConnectStatus('static');
    }
    setDbConnecting(false);
  };

  const activeFlow = flows.find(f => f.id === activeFlowId);

  /* ── COLLAPSED STATE ── */
  if (!isOpen) {
    return (
      <div style={{
        width: 36, minWidth: 36, height: '100%', background: '#fff',
        borderRight: '1px solid #E0E0E0', display: 'flex', flexDirection: 'column',
        alignItems: 'center', padding: '10px 0', gap: 8, zIndex: 10,
      }}>
        <ToggleBtn isOpen={false} onClick={onToggle} />
        {activeFlow && (
          <span title={activeFlow.name} style={{ width: 10, height: 10, borderRadius: '50%', background: activeFlow.color, boxShadow: `0 0 5px ${activeFlow.color}`, marginTop: 4 }} />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, marginTop: 4 }}>
          {flows.map(f => {
            const isActive = activeFlowId === f.id;
            return (
              <button key={f.id} onClick={() => handleFlowClick(f.id)} title={f.name}
                style={{ width: 10, height: 10, borderRadius: '50%', background: f.color, border: isActive ? '2px solid #212121' : '2px solid transparent', cursor: 'pointer', padding: 0, opacity: isActive ? 1 : 0.45 }} />
            );
          })}
        </div>
        {dbLayerVisible && (
          <span title="DB Layer ON" style={{ fontSize: 14, marginTop: 4 }}>🗄️</span>
        )}
      </div>
    );
  }

  /* ── OPEN STATE ── */
  const sectionHeader = (label: string, expanded: boolean, onClickFn: () => void) => (
    <button onClick={onClickFn} style={{ fontSize: 10, fontWeight: 700, color: '#757575', letterSpacing: '0.08em', textTransform: 'uppercase', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: expanded ? 8 : 4, display: 'flex', alignItems: 'center', gap: 4 }}>
      {expanded ? '▾' : '▸'} {label}
    </button>
  );

  return (
    <div style={{ width: 260, minWidth: 260, height: '100%', background: '#fff', borderRight: '1px solid #E0E0E0', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'system-ui, sans-serif', zIndex: 10 }}>
      {/* Header */}
      <div style={{ padding: '10px 10px 8px', borderBottom: '1px solid #F0F0F0', background: '#FAFAFA', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: '#9E9E9E', marginBottom: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {visibleCount}/{serviceNodes.length} services
            {activeFlowId && ` · ${flows.find(f => f.id === activeFlowId)?.name}`}
          </div>
          <input type="text" placeholder="Search services..." value={searchQuery} onChange={e => handleSearch(e.target.value)}
            style={{ width: '100%', border: '1px solid #E0E0E0', borderRadius: 6, padding: '5px 8px', fontSize: 12, outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
        </div>
        <ToggleBtn isOpen={true} onClick={onToggle} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 16px' }}>
        {/* Service Palette (OSS-NETVIZ-2): drag&drop типов сервисов на холст */}
        {canAddService && (
          <div style={{ padding: '10px 12px 4px' }}>
            {sectionHeader('Палитра сервисов', showPalette, () => setShowPalette(v => !v))}
            {showPalette && (
              <>
                <div style={{ fontSize: 10, color: '#9E9E9E', marginBottom: 6 }}>
                  Перетащите группу на холст, чтобы добавить сервис
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {PALETTE_GROUPS.map(g => (
                    <div
                      key={g}
                      draggable
                      onDragStart={e => {
                        e.dataTransfer.setData(SERVICE_PALETTE_MIME, g);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      title={`Перетащить «${g}» на холст`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '4px 8px', borderRadius: 6, cursor: 'grab',
                        border: `1px solid ${GROUP_COLORS[g]}`, background: `${GROUP_COLORS[g]}10`,
                        fontSize: 11, color: '#212121', userSelect: 'none',
                      }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: GROUP_COLORS[g], flexShrink: 0 }} />
                      {g}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Business Flows */}
        <div style={{ padding: '10px 12px 4px' }}>
          {sectionHeader('Business Flows', showFlows, () => setShowFlows(v => !v))}
          {showFlows && (() => {
            const groups: Record<string, typeof flows> = {};
            flows.forEach(f => {
              const g = f.group ?? 'Прочее';
              if (!groups[g]) groups[g] = [];
              groups[g].push(f);
            });
            return Object.entries(groups).map(([groupName, groupFlows]) => {
              const isGroupCollapsed = collapsedFlowGroups.has(groupName);
              const toggleGroup = () => setCollapsedFlowGroups(prev => {
                const next = new Set(prev);
                if (next.has(groupName)) next.delete(groupName); else next.add(groupName);
                return next;
              });
              return (
                <div key={groupName} style={{ marginBottom: 5 }}>
                  <button onClick={toggleGroup} style={{
                    display: 'flex', alignItems: 'center', gap: 4, width: '100%',
                    fontSize: 9, fontWeight: 700, color: '#9E9E9E', letterSpacing: '0.07em',
                    textTransform: 'uppercase', padding: '2px 4px 3px',
                    marginBottom: isGroupCollapsed ? 0 : 3,
                    background: 'none', border: 'none', borderBottom: '1px solid #F0F0F0',
                    cursor: 'pointer', textAlign: 'left',
                  }}>
                    <span>{isGroupCollapsed ? '▸' : '▾'}</span>
                    <span>{groupName}</span>
                  </button>
                  {!isGroupCollapsed && groupFlows.map(flow => {
                    const isActive = activeFlowId === flow.id;
                    const isDisabled = disabledFlowIds.has(flow.id);
                    return (
                      <button
                        key={flow.id}
                        onClick={() => !isDisabled && handleFlowClick(flow.id)}
                        title={isDisabled ? 'Сценарий недоступен: один из сервисов отключён' : flow.name}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                          padding: '5px 8px', marginBottom: 2,
                          background: isActive ? `${flow.color}18` : 'transparent',
                          border: isActive ? `1.5px solid ${flow.color}` : '1.5px solid transparent',
                          borderRadius: 6,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          textAlign: 'left', transition: 'all 0.15s',
                          opacity: isDisabled ? 0.4 : 1,
                        }}
                      >
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: isDisabled ? '#9E9E9E' : flow.color, flexShrink: 0 }} />
                        <span style={{ fontSize: 11, color: isDisabled ? '#9E9E9E' : '#212121', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: isDisabled ? 'line-through' : 'none' }}>{flow.name}</span>
                        {isActive && !isDisabled && <span style={{ fontSize: 9, color: flow.color, fontWeight: 700 }}>ON</span>}
                        {isDisabled && <span style={{ fontSize: 9, color: '#9E9E9E' }}>off</span>}
                      </button>
                    );
                  })}
                </div>
              );
            });
          })()}
        </div>

        {/* Zone Filters */}
        <div style={{ padding: '10px 12px 4px' }}>
          {sectionHeader('Zones', showZones, () => setShowZones(v => !v))}
          {showZones && ZONES.map(z => {
            const zc = ZONE_COLORS[z]; const active = zoneVisibility[z];
            return (
              <label key={z} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 4px', cursor: 'pointer', borderRadius: 4, marginBottom: 2, opacity: active ? 1 : 0.5 }}>
                <input type="checkbox" checked={active} onChange={() => toggleZone(z)} style={{ accentColor: zc.border }} />
                <span style={{ width: 8, height: 8, borderRadius: 2, background: zc.border, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: '#424242' }}>{ZONE_LABELS[z]}</span>
              </label>
            );
          })}
        </div>

        {/* Connector Types */}
        <div style={{ padding: '10px 12px 4px' }}>
          {sectionHeader('Connectors', showConnectors, () => setShowConnectors(v => !v))}
          {showConnectors && CONNECTOR_TYPES.map(ct => {
            const cs = CONNECTOR_STYLES[ct]; const active = connectorTypeVisibility[ct];
            return (
              <label key={ct} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 4px', cursor: 'pointer', borderRadius: 4, marginBottom: 2, opacity: active ? 1 : 0.5 }}>
                <input type="checkbox" checked={active} onChange={() => toggleConnectorType(ct)} style={{ accentColor: cs.color }} />
                <span style={{ width: 20, height: 3, background: cs.color, borderRadius: 2, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: '#424242' }}>{ct}</span>
              </label>
            );
          })}
        </div>

        {/* Partner Services */}
        {(() => {
          const staticPartners = ACTOR_NODES.filter(a => (a.data as ActorData).zone === 'zone-partners');
          const customPartners = customActors.filter(a => (a.data as ActorData).zone === 'zone-partners');
          const allPartners = [...staticPartners, ...customPartners];
          const customIds = new Set(customActors.map(a => a.id));
          return (
            <div style={{ padding: '10px 12px 4px', borderTop: '1px solid #F0F0F0', marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: showPartners ? 8 : 4 }}>
                <button onClick={() => setShowPartners(v => !v)}
                  style={{ fontSize: 10, fontWeight: 700, color: '#757575', letterSpacing: '0.08em', textTransform: 'uppercase', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
                  {showPartners ? '▾' : '▸'} Partner Services
                </button>
                <button onClick={onAddActorClick}
                  style={{ fontSize: 11, color: '#1976D2', background: 'none', border: '1px solid #BBDEFB', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  + Add
                </button>
              </div>
              {showPartners && allPartners.map(actor => {
                const aData = actor.data as ActorData;
                const visible = actorVisibility[actor.id] !== false;
                const isCustom = customIds.has(actor.id);
                return (
                  <div key={actor.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 4px', borderRadius: 4, marginBottom: 2, opacity: visible ? 1 : 0.45 }}>
                    <input type="checkbox" checked={visible} onChange={() => toggleActorVisibility(actor.id)}
                      style={{ accentColor: '#7B1FA2', cursor: 'pointer', flexShrink: 0 }} />
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6A1B9A', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#424242', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={aData.name}>
                      {aData.label}
                    </span>
                    {isCustom && (
                      <button onClick={() => removeCustomActor(actor.id)}
                        style={{ color: '#EF5350', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1, flexShrink: 0 }}
                        title="Remove partner service">
                        ✕
                      </button>
                    )}
                  </div>
                );
              })}
              {showPartners && allPartners.length === 0 && (
                <div style={{ fontSize: 11, color: '#9E9E9E', padding: '4px 4px' }}>No partner services</div>
              )}
            </div>
          );
        })()}

        {/* Database Layer */}
        <div style={{ padding: '10px 12px 4px', borderTop: '1px solid #F0F0F0', marginTop: 8 }}>
          {sectionHeader('Database Layer', showDbSection, () => setShowDbSection(v => !v))}
          {showDbSection && (
            <div>
              {/* DB Layer toggle */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 4px', cursor: 'pointer', marginBottom: 8 }}>
                <div onClick={toggleDbLayer} style={{
                  width: 32, height: 16, borderRadius: 8, background: dbLayerVisible ? '#4CAF50' : '#E0E0E0',
                  position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
                }}>
                  <div style={{ position: 'absolute', top: 2, left: dbLayerVisible ? 16 : 2, width: 12, height: 12, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                </div>
                <span style={{ fontSize: 12, color: '#424242', fontWeight: dbLayerVisible ? 600 : 400 }}>
                  {dbLayerVisible ? '🗄️ DB слой включён' : 'Показать DB слой'}
                </span>
              </label>

              {/* DB Connection Form toggle */}
              <button
                onClick={() => setShowDbForm(v => !v)}
                style={{ fontSize: 11, color: '#1565C0', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}
              >
                {showDbForm ? '▾' : '▸'} Подключение к БД
              </button>

              {showDbForm && (
                <div style={{ background: '#F8F9FA', borderRadius: 6, padding: '10px 10px', border: '1px solid #E0E0E0' }}>
                  {/* Service selector */}
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: '#757575', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Сервис</div>
                    <select
                      value={dbFormServiceId}
                      onChange={e => {
                        setDbFormServiceId(e.target.value);
                        const found = DB_SERVICES.find(s => s.id === e.target.value);
                        if (found) setDbName(found.dbName);
                      }}
                      style={{ width: '100%', border: '1px solid #E0E0E0', borderRadius: 4, padding: '4px 6px', fontSize: 11, outline: 'none', background: '#fff' }}
                    >
                      {DB_SERVICES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </div>

                  {/* Host + Port */}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: '#757575', marginBottom: 2 }}>Host</div>
                      <input value={dbHost} onChange={e => setDbHost(e.target.value)}
                        style={{ width: '100%', border: '1px solid #E0E0E0', borderRadius: 4, padding: '4px 6px', fontSize: 11, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ width: 52 }}>
                      <div style={{ fontSize: 10, color: '#757575', marginBottom: 2 }}>Port</div>
                      <input value={dbPort} onChange={e => setDbPort(e.target.value)}
                        style={{ width: '100%', border: '1px solid #E0E0E0', borderRadius: 4, padding: '4px 6px', fontSize: 11, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  </div>

                  {/* DB Name */}
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 10, color: '#757575', marginBottom: 2 }}>Database</div>
                    <input value={dbName} onChange={e => setDbName(e.target.value)}
                      style={{ width: '100%', border: '1px solid #E0E0E0', borderRadius: 4, padding: '4px 6px', fontSize: 11, outline: 'none', boxSizing: 'border-box' }} />
                  </div>

                  {/* User + Password */}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: '#757575', marginBottom: 2 }}>User</div>
                      <input value={dbUser} onChange={e => setDbUser(e.target.value)}
                        style={{ width: '100%', border: '1px solid #E0E0E0', borderRadius: 4, padding: '4px 6px', fontSize: 11, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: '#757575', marginBottom: 2 }}>Password</div>
                      <input type="password" value={dbPassword} onChange={e => setDbPassword(e.target.value)}
                        style={{ width: '100%', border: '1px solid #E0E0E0', borderRadius: 4, padding: '4px 6px', fontSize: 11, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  </div>

                  {/* Connect button */}
                  <button
                    onClick={handleDbConnect}
                    disabled={dbConnecting}
                    style={{
                      width: '100%', padding: '6px', borderRadius: 5,
                      background: dbConnecting ? '#E0E0E0' : '#1565C0',
                      color: dbConnecting ? '#757575' : '#fff',
                      border: 'none', cursor: dbConnecting ? 'default' : 'pointer',
                      fontSize: 12, fontWeight: 600,
                    }}
                  >
                    {dbConnecting ? 'Подключение...' : 'Подключить'}
                  </button>

                  {/* Status */}
                  {dbConnectStatus === 'connected' && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#2E7D32', textAlign: 'center' }}>✓ Подключено</div>
                  )}
                  {dbConnectStatus === 'static' && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#E65100', textAlign: 'center' }}>⚠ Используется статическая схема</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeftPanel;
